import React from 'react';
import { BarChart, Trash2, ClipboardEdit } from 'lucide-react';
import { formatNumber } from '../../../../../utils/formatting';
import Button from '../../../../../components/ui/Button';

/**
 * Компонент боковой панели для сравнения сохраненных профилей
 */
const ComparisonSidebar = ({ 
  savedProfiles, 
  deleteSavedProfile, 
  loadSavedProfile 
}) => {
  // Если нет сохраненных профилей
  if (!savedProfiles || savedProfiles.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
          <BarChart className="w-5 h-5 mr-2 text-indigo-500" />
          Сравнение профилей
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Здесь будут отображаться сохраненные профили для сравнения результатов расчета.
          Нажмите "Сохранить профиль" после расчета, чтобы добавить его сюда.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center">
          <BarChart className="w-5 h-5 mr-2 text-indigo-500" />
          Сравнение профилей
        </h3>
        <span className="text-sm text-gray-500 dark:text-gray-400">
          {savedProfiles.length} {getPluralProfiles(savedProfiles.length)}
        </span>
      </div>

      <div className="space-y-4">
        {savedProfiles.map((profile) => (
          <div 
            key={profile.id} 
            className="bg-gray-50 dark:bg-gray-700/30 border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors group"
          >
            <div className="flex justify-between items-start mb-2">
              <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                {profile.name}
              </h4>
              <div className="flex items-center space-x-2">
                <Button
                  size="sm"
                  variant="ghost"
                  icon={<ClipboardEdit className="w-4 h-4" />}
                  onClick={() => loadSavedProfile(profile.id)}
                  title="Загрузить параметры"
                />
                <Button
                  size="sm"
                  variant="ghost"
                  icon={<Trash2 className="w-4 h-4 text-red-500" />}
                  onClick={() => deleteSavedProfile(profile.id)}
                  title="Удалить"
                />
              </div>
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">
              <div className="flex justify-between mb-1">
                <span>Базальный метаболизм:</span>
                <span className="font-medium">
                  {formatNumber(profile.results.bmr, 0)} ккал
                </span>
              </div>
              <div className="flex justify-between mb-1">
                <span>Общий расход энергии:</span>
                <span className="font-medium">
                  {formatNumber(profile.results.tdee, 0)} ккал
                </span>
              </div>
              <div className="flex justify-between mb-1">
                <span>Целевые калории:</span>
                <span className="font-semibold text-emerald-600 dark:text-emerald-400">
                  {formatNumber(profile.results.targetCalories, 0)} ккал
                </span>
              </div>
              
              {/* Дополнительная информация (разворачивается при наведении) */}
              <div className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-600 opacity-0 h-0 overflow-hidden transition-all duration-300 group-hover:opacity-100 group-hover:h-auto">
                <div className="space-y-1 text-xs">
                  <div className="flex justify-between">
                    <span>Пол:</span>
                    <span>{profile.formData.gender === 'male' ? 'Мужской' : 'Женский'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Вес:</span>
                    <span>{profile.formData.weight} кг</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Рост:</span>
                    <span>{profile.formData.height} см</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Возраст:</span>
                    <span>{profile.formData.age} лет</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Активность:</span>
                    <span>{getActivityLabel(profile.formData.activityLevel)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Цель:</span>
                    <span>{getGoalLabel(profile.formData.goal)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>ИМТ:</span>
                    <span>{profile.results.bmi} кг/м²</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="mt-2">
              <span className="text-xs py-1 px-2 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-300 rounded-full">
                {getFormattedDate(profile.date)}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Вспомогательная функция для склонения слова "профиль"
const getPluralProfiles = (count) => {
  count = Math.abs(count);
  const lastDigit = count % 10;
  const lastTwoDigits = count % 100;
  
  if (lastTwoDigits >= 11 && lastTwoDigits <= 19) {
    return 'профилей';
  }
  
  if (lastDigit === 1) {
    return 'профиль';
  }
  
  if (lastDigit >= 2 && lastDigit <= 4) {
    return 'профиля';
  }
  
  return 'профилей';
};

// Функция для форматирования даты создания профиля
const getFormattedDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  });
};

// Функция для получения текстового описания уровня активности
const getActivityLabel = (activityLevel) => {
  switch (activityLevel) {
    case 'sedentary': return 'Сидячий';
    case 'light': return 'Легкая';
    case 'moderate': return 'Умеренная';
    case 'active': return 'Высокая';
    case 'very_active': return 'Очень высокая';
    default: return activityLevel;
  }
};

// Функция для получения текстового описания цели
const getGoalLabel = (goal) => {
  switch (goal) {
    case 'lose': return 'Снижение веса';
    case 'maintain': return 'Поддержание веса';
    case 'gain': return 'Набор массы';
    default: return goal;
  }
};

export default ComparisonSidebar;